import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { TrendingUp, Users, Calendar, Star, MessageSquare, ArrowUpRight, ArrowDownRight, DollarSign } from "lucide-react";
import { useCurrency } from "@/hooks/use-currency";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from "recharts";
import { Badge } from "@/components/ui/badge";

interface AnalyticsData {
  totalBookings: number;
  pendingBookings: number;
  completedBookings: number;
  totalRevenue: number;
  avgRating: number;
  totalReviews: number;
  unreadMessages: number;
  bookingsByStatus: Array<{ name: string; value: number }>;
  revenueByMonth: Array<{ month: string; revenue: number }>;
  recentBookings: Array<{
    id: string;
    customer_name: string;
    total_amount: number;
    booking_status: string;
    created_at: string;
  }>;
}

export function AnalyticsDashboard() {
  const { formatPrice } = useCurrency();
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      // Fetch bookings data
      const { data: bookings } = await supabase
        .from('bookings')
        .select('id, customer_name, booking_status, payment_status, total_amount, created_at')
        .order('created_at', { ascending: false });

      // Fetch reviews data
      const { data: reviews } = await supabase
        .from('reviews')
        .select('rating, is_approved');

      // Fetch unread messages
      const { data: messages } = await supabase
        .from('contact_messages')
        .select('id')
        .eq('is_read', false);

      // Calculate analytics
      const totalBookings = bookings?.length || 0;
      const pendingBookings = bookings?.filter(b => b.booking_status === 'pending').length || 0;
      const completedBookings = bookings?.filter(b => b.booking_status === 'completed').length || 0;

      const totalRevenue = bookings?.reduce((sum, b) => {
        if (b.payment_status === 'paid' || b.payment_status === 'partial') {
          return sum + Number(b.total_amount);
        }
        return sum;
      }, 0) || 0;

      const approvedReviews = reviews?.filter(r => r.is_approved) || [];
      const avgRating = approvedReviews.length > 0
        ? approvedReviews.reduce((sum, r) => sum + r.rating, 0) / approvedReviews.length
        : 0;

      // Prepare Chart Data: Bookings by Status
      const statusCounts = bookings?.reduce((acc, b) => {
        acc[b.booking_status] = (acc[b.booking_status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      const bookingsByStatus = Object.entries(statusCounts).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value
      }));

      // Prepare Chart Data: Revenue by Month (Last 6 Months)
      const revenueByMonth: Array<{ month: string; revenue: number }> = [];
      const now = new Date();
      for (let i = 5; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthName = date.toLocaleString('default', { month: 'short' });
        const monthRevenue = bookings?.filter(b => {
          const bookingDate = new Date(b.created_at);
          return bookingDate.getMonth() === date.getMonth() &&
            bookingDate.getFullYear() === date.getFullYear() &&
            (b.payment_status === 'paid' || b.payment_status === 'partial');
        }).reduce((sum, b) => sum + Number(b.total_amount), 0) || 0;

        revenueByMonth.push({ month: monthName, revenue: monthRevenue });
      }

      setAnalytics({
        totalBookings,
        pendingBookings,
        completedBookings,
        totalRevenue,
        avgRating,
        totalReviews: approvedReviews.length,
        unreadMessages: messages?.length || 0,
        bookingsByStatus,
        revenueByMonth,
        recentBookings: bookings?.slice(0, 5) || [] // Top 5 recent bookings
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!analytics) return null;

  const STATUS_COLORS = {
    Pending: '#fbbf24', // yellow-400
    Confirmed: '#22c55e', // green-500
    Completed: '#3b82f6', // blue-500
    Cancelled: '#ef4444', // red-500
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Dashboard Overview</h2>
          <p className="text-muted-foreground mt-1">
            Welcome back! Here's what's happening with your business.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Optional: Add date range picker here later */}
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatPrice(analytics.totalRevenue)}</div>
            <p className="text-xs text-muted-foreground mt-1 flex items-center">
              <span className="text-green-500 flex items-center mr-1">
                <ArrowUpRight className="h-3 w-3" /> +12.5%
              </span>
              from last month (mock)
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Bookings</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalBookings}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {analytics.pendingBookings} pending approval
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Client Rating</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.avgRating.toFixed(1)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Based on {analytics.totalReviews} reviews
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unread Messages</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.unreadMessages}</div>
            <p className="text-xs text-muted-foreground mt-1 flex items-center">
              Action needed
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">

        {/* Revenue Trend Chart */}
        <Card className="col-span-4 hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle>Revenue Overview</CardTitle>
            <CardDescription>Monthly revenue for the last 6 months</CardDescription>
          </CardHeader>
          <CardContent className="pl-2">
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analytics.revenueByMonth}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="month"
                    stroke="#888888"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="#888888"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `$${value}`}
                  />
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    formatter={(value) => formatPrice(value as number)}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#8b5cf6"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorRevenue)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Bookings Status Chart */}
        <Card className="col-span-3 hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle>Booking Status</CardTitle>
            <CardDescription>Distribution of current bookings</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.bookingsByStatus} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <XAxis type="number" hide />
                  <YAxis
                    dataKey="name"
                    type="category"
                    axisLine={false}
                    tickLine={false}
                    fontSize={12}
                    width={80}
                  />
                  <Tooltip
                    cursor={{ fill: 'transparent' }}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={32}>
                    {analytics.bookingsByStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.name as keyof typeof STATUS_COLORS] || '#94a3b8'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity Table */}
      <Card className="hover:shadow-md transition-shadow">
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Latest booking requests and updates</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead className="text-right">Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {analytics.recentBookings.map((booking) => (
                <TableRow key={booking.id}>
                  <TableCell className="font-medium">{booking.customer_name}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`
                        ${booking.booking_status === 'confirmed' ? 'bg-green-50 text-green-700 border-green-200' :
                          booking.booking_status === 'pending' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                            booking.booking_status === 'cancelled' ? 'bg-red-50 text-red-700 border-red-200' :
                              'bg-blue-50 text-blue-700 border-blue-200'}
                      `}
                    >
                      {booking.booking_status.charAt(0).toUpperCase() + booking.booking_status.slice(1)}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatPrice(booking.total_amount)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {new Date(booking.created_at).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))}
              {analytics.recentBookings.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                    No recent activity found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
